/**
 * Messages — Figma frame 175:35 (3-column layout).
 *
 *   Left     : threads list (search + unread total + per-thread preview)
 *   Middle   : active thread — student header, day divider, message bubbles,
 *              two-row composer (Write/Preview tabs + textarea + Send)
 *   Right    : student context sidebar (avatar + risk chip + metric stack +
 *              active flags + quick actions)
 *
 * The full ConversationThread component (with all its scroll/WS/optimistic
 * behaviour) still exists for other surfaces, but this route re-implements a
 * leaner variant inline so we can shape it to the Figma exactly. Backend
 * contract is preserved: same hooks (useConversations / useMessages /
 * useSendMessage / useMarkAsRead / useMessagesWebSocket).
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format, formatDistanceToNowStrict } from 'date-fns';
import {
  useConversation,
  useConversations,
  useMarkAsRead,
  useMessages,
  useMessagesWebSocket,
  useSendMessage,
  useStartConversation,
  type OptimisticMessage,
} from '../hooks/useMessages';
import { useStudentDetail } from '../hooks';
import { useAtRisk } from '../hooks/useAtRisk';
import { useToast } from '../components/Toast';
import styles from './Messages.module.css';

function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  if (parts.length === 0) return 'ST';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function relTime(iso: string): string {
  if (!Number.isFinite(dateValue(iso))) return 'Date unavailable';
  try {
    return formatDistanceToNowStrict(new Date(iso))
      .replace('minutes', 'm')
      .replace('minute', 'm')
      .replace('hours', 'h')
      .replace('hour', 'h')
      .replace('days', 'd')
      .replace('day', 'd')
      .replace('seconds', 's')
      .replace('second', 's')
      .replace(/\s/g, '');
  } catch {
    return 'Date unavailable';
  }
}

function bubbleTime(iso: string): string {
  return Number.isFinite(dateValue(iso)) ? format(new Date(iso), 'HH:mm') : 'Time unavailable';
}

function dateValue(iso?: string): number {
  return typeof iso === 'string' && iso.trim() ? new Date(iso).getTime() : NaN;
}

function activityLabel(iso?: string): string {
  return Number.isFinite(dateValue(iso))
    ? formatDistanceToNowStrict(new Date(iso!), { addSuffix: true })
    : 'Unavailable';
}

function messageDay(iso: string): string {
  return Number.isFinite(dateValue(iso))
    ? format(new Date(iso), 'MMM d, yyyy')
    : 'Date unavailable';
}

const Messages: React.FC<{ heading?: string; supportQueue?: React.ReactNode; supportContext?: React.ReactNode }> = ({ heading = 'Messages', supportQueue, supportContext }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationIdParam = searchParams.get('conversationId');
  const studentIdParam = searchParams.get('studentId');

  const [search, setSearch] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 720px)').matches,
  );
  const [composerTab, setComposerTab] = useState<'write' | 'preview'>('write');
  const [draftState, setDraftState] = useState({ conversationId: conversationIdParam, body: '' });
  // Derive the visible draft from its recipient on the very first render of a
  // route change, before the reset effect runs. Never retarget another draft.
  const draft = draftState.conversationId === conversationIdParam ? draftState.body : '';
  const setDraft = (body: string) => setDraftState({ conversationId: conversationIdParam, body });
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const openingStudentRef = useRef<string | null>(null);
  const bootstrapRoute = searchParams.toString();
  const bootstrapIntentRef = useRef({ route: bootstrapRoute, generation: 0 });
  if (bootstrapIntentRef.current.route !== bootstrapRoute) {
    bootstrapIntentRef.current = { route: bootstrapRoute, generation: bootstrapIntentRef.current.generation + 1 };
    openingStudentRef.current = null;
  }
  const bootstrapRequestRef = useRef(0);
  const bootstrapMountedRef = useRef(true);
  const listSearchRef = useRef<HTMLInputElement>(null);
  const conversationButtonsRef = useRef(new Map<string, HTMLButtonElement>());
  const returnFocusRef = useRef<string | null>(null);
  const threadPaneRef = useRef<HTMLElement>(null);
  const threadHeadingRef = useRef<HTMLHeadingElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(max-width: 720px)');
    const update = () => setIsMobile(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useLayoutEffect(() => {
    if (!isMobile || !pageRef.current) return;
    const page = pageRef.current;
    // The shell header wraps on phones. Measure its actual occupied space
    // rather than assuming the desktop's 64px header height.
    const measure = () => page.style.setProperty(
      '--messages-viewport-offset',
      `${Math.max(0, Math.round(page.getBoundingClientRect().top))}px`,
    );
    measure();
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    const shellHeader = page.parentElement?.previousElementSibling;
    if (shellHeader instanceof HTMLElement) observer?.observe(shellHeader);
    return () => {
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
      observer?.disconnect();
      page.style.removeProperty('--messages-viewport-offset');
    };
  }, [isMobile]);

  useMessagesWebSocket();

  const { data: conversations, isLoading, isError, refetch: refetchConversations } = useConversations({
    unreadOnly: unreadOnly || undefined,
  });
  const startConversation = useStartConversation();

  useEffect(() => {
    bootstrapMountedRef.current = true;
    return () => { bootstrapMountedRef.current = false; };
  }, []);

  const startStudentConversation = (studentId: string) => {
    const intent = bootstrapIntentRef.current;
    const request = ++bootstrapRequestRef.current;
    startConversation.mutate(studentId, {
      onSuccess: (conversation) => {
        // A completed start is not permission to replace a newer route intent,
        // even if navigation returned A→B→A or this was an explicit retry.
        if (!bootstrapMountedRef.current || bootstrapIntentRef.current !== intent || bootstrapRequestRef.current !== request || conversation.studentId !== studentId) return;
        setSearchParams({ conversationId: conversation.id }, { replace: true });
      },
    });
  };

  // ?studentId=... bootstrap
  useEffect(() => {
    if (!studentIdParam || isLoading || isError) return;
    const existing = conversations?.find((c) => c.studentId === studentIdParam);
    if (existing) {
      setSearchParams({ conversationId: existing.id }, { replace: true });
      return;
    }
    if (openingStudentRef.current === studentIdParam) return;
    openingStudentRef.current = studentIdParam;
    startStudentConversation(studentIdParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentIdParam, conversations, isLoading, isError]);

  const filteredConversations = useMemo(() => {
    if (!conversations) return [];
    const term = search.trim().toLowerCase();
    let list = conversations;
    if (term) {
      list = list.filter(
        (c) =>
          c.studentName.toLowerCase().includes(term) ||
          c.studentEmail.toLowerCase().includes(term) ||
          c.lastMessagePreview.toLowerCase().includes(term),
      );
    }
    return [...list].sort(
      (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime(),
    );
  }, [conversations, search]);

  const unreadTotal = useMemo(
    () => (conversations ?? []).reduce((sum, c) => sum + c.unreadCount, 0),
    [conversations],
  );

  const selectedConversation = filteredConversations.find(
    (c) => c.id === conversationIdParam,
  ) ?? (conversations ?? []).find((c) => c.id === conversationIdParam) ?? null;

  const {
    data: conversation,
    isLoading: conversationLoading,
    isError: conversationError,
    refetch: refetchConversation,
  } = useConversation(conversationIdParam ?? undefined);
  const activeConversation = conversationError ? null :
    (conversation?.id === conversationIdParam ? conversation : null) ?? selectedConversation;

  const { data: messagesPages, isLoading: messagesLoading, isError: messagesError, refetch: refetchMessages,
    hasNextPage, fetchNextPage, isFetchingNextPage } = useMessages(conversationIdParam ?? undefined);
  const sendMessage = useSendMessage();
  const markAsRead = useMarkAsRead();
  const toast = useToast();

  useEffect(() => {
    setDraftState({ conversationId: conversationIdParam, body: '' });
    setComposerTab('write');
  }, [conversationIdParam]);

  useEffect(() => {
    if (!isMobile) return;
    if (conversationIdParam) {
      (threadHeadingRef.current ?? threadPaneRef.current)?.focus();
    } else if (returnFocusRef.current) {
      (conversationButtonsRef.current.get(returnFocusRef.current) ?? listSearchRef.current)?.focus();
      returnFocusRef.current = null;
    }
  }, [isMobile, conversationIdParam, activeConversation?.id]);

  // Flatten pages (newest first per page) into an oldest-first array for display.
  const orderedMessages = useMemo<OptimisticMessage[]>(() => {
    if (!messagesPages) return [];
    const flat = messagesPages.pages.flatMap(page => page.messages).filter((message) => message.conversationId === conversationIdParam);
    return [...flat].sort(
      (a, b) => (Number.isFinite(dateValue(a.sentAt)) ? dateValue(a.sentAt) : Infinity)
        - (Number.isFinite(dateValue(b.sentAt)) ? dateValue(b.sentAt) : Infinity),
    );
  }, [messagesPages, conversationIdParam]);

  // Mark unread student messages as read once the conversation opens.
  useEffect(() => {
    if (!activeConversation || messagesError || !orderedMessages.length) return;
    const unread = orderedMessages
      .filter((m) => m.senderRole === 'student' && !m.readAt)
      .map((m) => m.id);
    if (unread.length > 0) {
      markAsRead.mutate({ conversationId: activeConversation.id, messageIds: unread });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversation?.id, orderedMessages.length, messagesError]);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [orderedMessages.length, activeConversation?.id]);

  const selectConversation = (id: string) => {
    setSearchParams({ conversationId: id });
  };

  const showConversations = () => {
    returnFocusRef.current = conversationIdParam;
    const next = new URLSearchParams(searchParams);
    next.delete('conversationId');
    next.delete('studentId');
    setSearchParams(next);
  };

  const handleSend = () => {
    const body = draft.trim();
    if (!body || !activeConversation || activeConversation.id !== conversationIdParam || messagesError || sendMessage.isPending || body.length > 4000) return;
    // The optimistic bubble is marked `failed` by the mutation on error; also
    // surface a toast so a send failure is never silently swallowed.
    sendMessage.mutate(
      { conversationId: activeConversation.id, body },
      {
        onError: () =>
          toast.showError('Message failed to send — tap Retry on the message to try again.'),
      },
    );
    setDraft('');
    composerRef.current?.focus();
  };

  // Retry a previously-failed optimistic message (reuses its clientId so the
  // hook replaces the failed copy in place rather than duplicating it).
  const handleRetry = (failed: OptimisticMessage) => {
    if (!activeConversation || messagesError || failed.conversationId !== activeConversation.id || !failed.clientId) return;
    sendMessage.mutate(
      { conversationId: activeConversation.id, body: failed.body, clientId: failed.clientId },
      {
        onError: () => toast.showError('Still couldn’t send — check your connection.'),
      },
    );
  };

  const handleComposerKey: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if ((e.key === 'Enter' && (e.metaKey || e.ctrlKey)) || (e.key === 'Enter' && !e.shiftKey && !composerTab.includes('preview'))) {
      // Enter → send; Shift+Enter → newline; ⌘/Ctrl+Enter also sends.
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    }
  };

  // ---------- Context sidebar data ----------
  const activeStudentId = activeConversation?.studentId;
  const {
    data: contextData,
    isLoading: contextLoading,
    isError: contextError,
    refetch: refetchContext,
  } = useStudentDetail(activeStudentId ?? '');
  // A cached/late response for another student must never populate this thread.
  const studentDetail = !contextError && contextData?.id === activeStudentId ? contextData : undefined;
  const contextFallback = contextLoading ? 'Loading…' : 'Unavailable';
  const lastActive = contextLoading ? 'Loading…' : activityLabel(studentDetail?.lastActiveAt);
  const {
    data: atRiskList,
    isLoading: riskLoading,
    isError: riskError,
    refetch: refetchRisk,
  } = useAtRisk();
  const atRiskRecord = !riskError ? atRiskList?.find((s) => s.studentId === activeStudentId) : undefined;
  const isFlagged = !!atRiskRecord;

  return (
    <div className={styles.page} ref={pageRef}>
      {/* ---------- Threads list ---------- */}
      <aside
        className={`${styles.listPane} ${!conversationIdParam ? styles.mobileShow : ''}`}
        aria-label="Conversations"
        hidden={isMobile && !!conversationIdParam}
      >
        <header className={styles.listHeader}>
          <h1 className={styles.listTitle}>{heading}</h1>
          {unreadTotal > 0 && (
            <span className={styles.unreadTotal} aria-label={`${unreadTotal} unread`}>
              {unreadTotal}
            </span>
          )}
        </header>

        {supportQueue}

        <div className={styles.listSearch}>
          <input
            type="search"
            ref={listSearchRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search conversations…"
            className={styles.searchInput}
            aria-label="Search conversations"
          />
        </div>

        <div className={styles.unreadToggleRow}>
          <label className={styles.unreadToggle}>
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
            />
            Unread only
          </label>
        </div>

        {isLoading ? (
          <div className={styles.listEmpty} role="status">Loading conversations…</div>
        ) : isError ? (
          <div className={styles.listEmpty} role="alert">Couldn&apos;t load conversations. <button type="button" onClick={() => refetchConversations()}>Retry</button></div>
        ) : filteredConversations.length === 0 ? (
          <div className={styles.listEmpty}>
            {search.trim() ? 'No conversations match that search.' : unreadOnly ? 'No unread conversations.' : 'No conversations yet.'}
          </div>
        ) : (
          <ul className={styles.list}>
            {filteredConversations.map((c) => {
              const active = c.id === conversationIdParam;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    ref={(element) => {
                      if (element) conversationButtonsRef.current.set(c.id, element);
                      else conversationButtonsRef.current.delete(c.id);
                    }}
                    onClick={() => selectConversation(c.id)}
                    aria-label={`Open conversation with ${c.studentName}`}
                    aria-controls="messages-active-thread"
                    aria-expanded={active}
                    className={`${styles.listItem} ${active ? styles.listItemActive : ''}`}
                    aria-current={active ? 'true' : undefined}
                  >
                    <div className={styles.listRow}>
                      <span className={styles.avatar} aria-hidden="true">
                        {initialsFrom(c.studentName)}
                      </span>
                      <div className={styles.listBody}>
                        <div className={styles.listItemHeader}>
                          <span className={styles.listItemName}>{c.studentName}</span>
                          <span className={styles.listItemTime}>{relTime(c.lastMessageAt)}</span>
                        </div>
                        <div className={styles.listItemPreviewRow}>
                          <span
                            className={`${styles.listItemPreview} ${c.unreadCount > 0 ? styles.listItemPreviewUnread : ''}`}
                          >
                            {c.lastMessagePreview}
                          </span>
                          {c.unreadCount > 0 && (
                            <span className={styles.unreadBadge}>{c.unreadCount}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </aside>

      {/* ---------- Active thread ---------- */}
      <main
        id="messages-active-thread"
        ref={threadPaneRef}
        tabIndex={-1}
        className={`${styles.threadPane} ${conversationIdParam ? styles.mobileShow : ''}`}
        aria-label="Active conversation"
        hidden={isMobile && !conversationIdParam}
      >
        {isMobile && conversationIdParam && (
          <nav className={styles.mobileNavigation} aria-label="Conversation navigation">
            <button type="button" onClick={showConversations}>
              <span aria-hidden="true">←</span> Back to conversations
            </button>
          </nav>
        )}
        {!activeConversation ? (
          <div className={styles.emptyThread}>
            {conversationIdParam && conversationLoading ? (
              <p role="status">Loading conversation…</p>
            ) : conversationIdParam && conversationError ? (
              <div role="alert">
                <p>Couldn&apos;t load this conversation.</p>
                <button type="button" onClick={() => refetchConversation()}>Retry</button>
              </div>
            ) : conversationIdParam ? 'Conversation unavailable.' : 'Select a conversation to open the thread.'}
          </div>
        ) : (
          <>
            <header className={styles.threadHeader}>
              <span className={styles.avatar} aria-hidden="true">
                {initialsFrom(activeConversation.studentName)}
              </span>
              <div className={styles.threadHeaderCopy}>
                <h2 className={styles.threadName} ref={threadHeadingRef} tabIndex={-1}>{activeConversation.studentName}</h2>
                <span className={styles.threadSubtitle}>
                  Last active: {lastActive}
                </span>
              </div>
              <Link
                to={`/students/${activeConversation.studentId}`}
                className={styles.viewProfile}
              >
                View profile
              </Link>
            </header>

            {supportContext}

            <div className={styles.transcript} ref={scrollRef} role="log" aria-live="polite">
              {!messagesError && hasNextPage && <button type="button" disabled={isFetchingNextPage}
                onClick={() => void fetchNextPage()}>{isFetchingNextPage ? 'Loading earlier messages…' : 'Load earlier messages'}</button>}
              {!messagesError && orderedMessages.map((m: OptimisticMessage, index) => {
                const day = messageDay(m.sentAt);
                const startsDay = index === 0 || messageDay(orderedMessages[index - 1].sentAt) !== day;
                const outgoing = m.senderRole === 'instructor';
                return (
                  <React.Fragment key={m.id}>
                    {startsDay && <div className={styles.dayDivider}>{day}</div>}
                    {m.senderRole === 'system' ? (
                      <div className={styles.bubbleSystem}>{m.body}</div>
                    ) : (
                      <div
                        className={`${styles.bubbleWrap} ${outgoing ? styles.bubbleWrapOut : styles.bubbleWrapIn}`}
                      >
                        <div
                          className={`${styles.bubble} ${outgoing ? styles.bubbleOut : styles.bubbleIn} ${
                            m.clientStatus === 'failed' ? styles.bubbleFailed : ''
                          }`}
                        >
                          {m.body}
                        </div>
                        <span className={styles.bubbleTime}>
                          {bubbleTime(m.sentAt)}
                          {m.clientStatus === 'sending' && ' · sending…'}
                          {m.clientStatus === 'failed' && ' · failed'}
                          {m.clientStatus === 'failed' && (
                            <button
                              type="button"
                              onClick={() => handleRetry(m)}
                              style={{
                                marginLeft: 6,
                                padding: 0,
                                border: 'none',
                                background: 'none',
                                color: '#dc2626',
                                font: 'inherit',
                                fontWeight: 600,
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            >
                              Retry
                            </button>
                          )}
                        </span>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              {messagesLoading && <p role="status">Loading messages…</p>}
              {messagesError && <div role="alert"><p>Couldn&apos;t load this conversation.</p><button type="button" onClick={() => refetchMessages()}>Retry</button></div>}
              {!messagesLoading && !messagesError && orderedMessages.length === 0 && (
                <div className={styles.emptyThread}>
                  No messages yet — say hi.
                </div>
              )}
            </div>

            <div className={styles.composer}>
              <div className={styles.composerTop}>
                <div className={styles.segmented}>
                  <button
                    type="button"
                    className={`${styles.segmentedItem} ${composerTab === 'write' ? styles.segmentedItemActive : ''}`}
                    aria-pressed={composerTab === 'write'}
                    onClick={() => setComposerTab('write')}
                  >
                    Write
                  </button>
                  <button
                    type="button"
                    className={`${styles.segmentedItem} ${composerTab === 'preview' ? styles.segmentedItemActive : ''}`}
                    aria-pressed={composerTab === 'preview'}
                    onClick={() => setComposerTab('preview')}
                  >
                    Preview
                  </button>
                </div>
                <span className={styles.composerHint}>
                  Plain text · ⌘↵ to send
                </span>
                <button
                  type="button"
                  className={styles.sendBtn}
                  onClick={handleSend}
                  disabled={!draft.trim() || sendMessage.isPending || messagesError}
                >
                  {sendMessage.isPending ? 'Sending…' : 'Send'}
                </button>
              </div>
              {composerTab === 'write' ? (
                <textarea
                  ref={composerRef}
                  disabled={messagesError}
                  maxLength={4000}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleComposerKey}
                  placeholder={`Reply to ${activeConversation.studentName.split(' ')[0]}…`}
                  className={styles.composerTextarea}
                  aria-label="Message body"
                />
              ) : (
                <div
                  className={styles.composerTextarea}
                  style={{ whiteSpace: 'pre-wrap', overflow: 'auto' }}
                >
                  {draft || 'Nothing to preview yet.'}
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* ---------- Context sidebar ---------- */}
      <aside className={styles.contextPane} aria-label="Student context">
        {!activeConversation ? (
          <div className={styles.contextEmpty}>
            Pick a conversation to see context.
          </div>
        ) : (
          <>
            <span className={styles.contextAvatar} aria-hidden="true">
              {initialsFrom(activeConversation.studentName)}
            </span>
            <div>
              <h3 className={styles.contextName}>{activeConversation.studentName}</h3>
              <p className={styles.contextSubtitle}>
                {activeConversation.studentEmail}
              </p>
              {isFlagged && <span className={styles.contextChip}>At risk</span>}
            </div>

            <div className={styles.contextDivider} />

            {contextLoading && <p role="status">Loading student context…</p>}
            {contextError && (
              <div role="alert">
                <p>Couldn&apos;t load student context.</p>
                <button type="button" onClick={() => refetchContext()}>Retry context</button>
              </div>
            )}
            {!contextLoading && !contextError && !studentDetail && <p>Student context unavailable.</p>}
            {riskLoading && <p role="status">Loading risk signals…</p>}
            {riskError && (
              <div role="alert">
                <p>Risk signals unavailable.</p>
                <button type="button" onClick={() => refetchRisk()}>Retry signals</button>
              </div>
            )}

            <div className={styles.contextMetric}>
              <span className={styles.contextLabel}>Average recorded score</span>
              <span className={styles.contextValue}>
                {studentDetail && typeof studentDetail.averageScore === 'number' && Number.isFinite(studentDetail.averageScore) && studentDetail.averageScore >= 0 && studentDetail.averageScore <= 100
                  ? `${studentDetail.averageScore}%` : studentDetail?.averageScore === null ? 'No recorded scores' : contextFallback}
              </span>
            </div>
            <div className={styles.contextDivider} />

            <div className={styles.contextMetric}>
              <span className={styles.contextLabel}>Last active</span>
              <span className={styles.contextValue}>
                {lastActive}
              </span>
            </div>
            <div className={styles.contextDivider} />

            <div className={styles.contextMetric}>
              <span className={styles.contextLabel}>Labs in progress</span>
              <span className={styles.contextValue}>
                {studentDetail && Number.isInteger(studentDetail.labsInProgress) && studentDetail.labsInProgress >= 0 ? studentDetail.labsInProgress : contextFallback}
              </span>
            </div>
            <div className={styles.contextDivider} />

            <div className={styles.contextMetric}>
              <span className={styles.contextLabel}>Account created</span>
              <span className={styles.contextValue}>
                {Number.isFinite(dateValue(studentDetail?.createdAt))
                  ? format(new Date(studentDetail!.createdAt), 'MMM d, yyyy')
                  : contextFallback}
              </span>
            </div>

            {atRiskRecord && atRiskRecord.reasons.length > 0 && (
              <>
                <div className={styles.contextDivider} />
                <div className={styles.flagsSection}>
                  <h4 className={styles.flagsTitle}>Active flags</h4>
                  {atRiskRecord.reasons.map((r) => (
                    <span key={r.type} className={styles.flagChip}>{r.detail}</span>
                  ))}
                </div>
              </>
            )}

            <div className={styles.contextDivider} />

            <div className={styles.quickActions}>
              <h4 className={styles.quickTitle}>Quick actions</h4>
              <button
                type="button"
                className={styles.quickPrimary}
                disabled
                aria-describedby="booking-unavailable"
              >
                Book 1:1
              </button>
              <p id="booking-unavailable" className={styles.quickUnavailable}>
                1:1 booking is not available in this portal.
              </p>
              <Link
                to={`/students/${encodeURIComponent(activeConversation.studentId)}#notes`}
                className={styles.quickGhost}
              >
                Add a note
              </Link>
              <Link
                to={`/students/${activeConversation.studentId}`}
                className={styles.quickGhost}
              >
                Open profile
              </Link>
            </div>
          </>
        )}
      </aside>

      {startConversation.isError && studentIdParam && <div role="alert" style={{ padding: 16 }}><p>Couldn&apos;t open a conversation with this student.</p><button type="button" disabled={startConversation.isPending} onClick={() => startStudentConversation(studentIdParam)}>Retry</button></div>}
    </div>
  );
};

export default Messages;
